import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Grid, Card, CardContent, CardActions, Box, Button,
  Chip, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert, CircularProgress,
  IconButton, Tabs, Tab, Divider, Checkbox, FormControlLabel, Snackbar
} from '@mui/material';
import {
  Search, LocationOn, Category, BookmarkBorder, Bookmark, Send, AccessTime,
  Stars, Warning, AutoAwesome, Visibility, FactCheck, CheckCircle, Edit,
  DeleteOutline, Image, InsertDriveFile, Download, ChatBubbleOutline, EventAvailable,
  Share as ShareIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import AIBidGenerator from '../components/bids/AIBidGenerator';
import MediaViewer from '../components/projects/MediaViewer';
import { useAuth } from '../contexts/AuthContext';
import { INTRO_TEMPLATES } from '../utils/contractorIntro';
import { useNavigate, useSearchParams } from 'react-router-dom';
import contractorMarketplaceService from '../services/contractorMarketplaceService';
import api from '../services/api';
import { formatHarare, harareInputToIso, isoToHarareInput } from '../utils/time';
import { coarseLocation, coarseTitle } from '../utils/location';
import { structuredBriefRows, parseStructuredBrief, answeredBriefCount } from '../utils/structuredBrief';
import { shareProfileLink } from '../utils/shareLink';
import useProfileGate from '../hooks/useProfileGate';
import ProfileGateDialog from '../components/ProfileGateDialog';
import DialogCloseButton from '../components/common/DialogCloseButton';

const ASSESSMENT_OPTIONS = [
  { value: 'site_visit', label: 'Site visit', helper: 'Inspect the actual kitchen, house, access, utilities, and existing condition.' },
  { value: 'building_plans', label: 'Building plans / drawings', helper: 'Review layout, dimensions, elevations, and structural or services notes.' },
  { value: 'photos_videos', label: 'Photos or videos', helper: 'See the current space, problem areas, and expected finish quality.' },
  { value: 'measurements', label: 'Measurements', helper: 'Confirm cabinet runs, countertop area, room size, openings, and service points.' },
  { value: 'material_specs', label: 'Material / finish specifications', helper: 'Confirm cabinets, countertops, fixtures, appliances, tiles, paint, and lighting.' },
  { value: 'boq_schedule', label: 'BOQ or material schedule', helper: 'Review quantities and itemized scope before pricing.' },
  { value: 'client_call', label: 'Client call', helper: 'Clarify priorities, budget expectations, access, and timing.' },
  { value: 'verifier_report', label: 'Verifier or PM report', helper: 'Use a trusted field report when a direct visit is not practical.' }
];

const PROJECT_TYPE_LABELS = {
  residential: 'Residential',
  commercial: 'Commercial',
  renovation: 'Renovation',
  infrastructure: 'Infrastructure',
  new_build: 'New build',
  extension: 'Extension',
  repair: 'Repair',
  maintenance: 'Maintenance'
};

const MARKETPLACE_TABS = {
  all: 0,
  saved: 1,
  requests: 2,
  recommended: 3
};

const getInitialMarketplaceTab = (tabParam) => MARKETPLACE_TABS[tabParam] ?? 3;

const ContractorMarketplacePage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const marketplaceService = contractorMarketplaceService;
  const {
    checkAction,
    showGateDialog,
    gateAction,
    closeGateDialog,
    profileComplete,
    profileData,
    hasVerifiablePortfolio
  } = useProfileGate();
  
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assessmentError, setAssessmentError] = useState('');
  
  const [activeTab, setActiveTab] = useState(() => getInitialMarketplaceTab(searchParams.get('tab')));
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    category: '',
    minBudget: '',
    maxBudget: '',
    location: '',
    coversTravel: false,
    sortBy: 'created_at',
    order: 'desc'
  });
  
  const [assessmentDialog, setAssessmentDialog] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingAssessmentRequest, setEditingAssessmentRequest] = useState(null);
  const [assessmentRequest, setAssessmentRequest] = useState({
    assessmentItems: ['site_visit'],
    requestMessage: '',
    availabilityNotes: '',
    availabilityWindows: ['', '', ''],
    visitFeeAmount: '',
    visitFeeDescription: ''
  });
  
  const [savedProjects, setSavedProjects] = useState([]);
  const [myProposals, setMyProposals] = useState([]);
  const [assessmentRequests, setAssessmentRequests] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [aiBidDialog, setAiBidDialog] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [responseDialogRequest, setResponseDialogRequest] = useState(null);
  const [acceptingWindow, setAcceptingWindow] = useState(null);
  const [completeVisitRequest, setCompleteVisitRequest] = useState(null);
  const [completeVisitPhotos, setCompleteVisitPhotos] = useState([]);
  const [completeVisitBidBy, setCompleteVisitBidBy] = useState('');
  const [completeVisitError, setCompleteVisitError] = useState('');
  const [completingVisit, setCompletingVisit] = useState(false);
  const [rescheduleRequest, setRescheduleRequest] = useState(null);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleError, setRescheduleError] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [responseFiles, setResponseFiles] = useState([]);
  const [responseFilesLoading, setResponseFilesLoading] = useState(false);
  const [responseFilesError, setResponseFilesError] = useState('');
  // Owner reference photos shown on the project detail dialog so contractors can
  // quote accurately before requesting an assessment.
  const [briefPhotos, setBriefPhotos] = useState([]);
  const [briefPhotosLoading, setBriefPhotosLoading] = useState(false);
  const [selectedBriefPhoto, setSelectedBriefPhoto] = useState(null);
  const [briefDocuments, setBriefDocuments] = useState([]);
  const [selectedResponseMedia, setSelectedResponseMedia] = useState(null);

  useEffect(() => {
    loadMarketplace();
  }, [filters, searchTerm]);

  useEffect(() => {
    const nextTab = getInitialMarketplaceTab(searchParams.get('tab'));
    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role !== 'contractor') return;

    localStorage.setItem('eqaya_marketplace_intro_seen', 'true');
    if (!user?.profile?.browsedProjects && user?.profile?.browsed_projects !== true) {
      updateUser({
        profile: {
          ...user?.profile,
          browsedProjects: true
        }
      });
    }
    api.put('/auth/users/profile', { browsed_projects: true }).catch(() => {});
  }, [user?.id, user?.role, user?.profile?.browsedProjects, user?.profile?.browsed_projects, updateUser]);

  useEffect(() => {
    if (activeTab === 1) {
      loadSavedProjects();
    } else if (activeTab === 2) {
      loadMyProposals();
    } else if (activeTab === 3) {
      loadRecommended();
    }
  }, [activeTab]);

  // Load the contractor's existing assessment requests up front (not only when the
  // My Requests tab opens), so any project that already has a request routes to
  // Edit instead of the create dead-end ("You have already requested an assessment").
  useEffect(() => {
    if (user?.role !== 'contractor') return undefined;
    let active = true;
    marketplaceService.getMyAssessmentRequests()
      .then((res) => {
        if (!active) return;
        const list = res?.data?.assessmentRequests || res?.data?.assessment_requests || res?.assessmentRequests || [];
        setAssessmentRequests(Array.isArray(list) ? list : []);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [user?.id, user?.role]);

  useEffect(() => {
    let isMounted = true;

    const loadResponseFiles = async () => {
      if (!responseDialogRequest?.project_id) {
        setResponseFiles([]);
        setResponseFilesError('');
        setResponseFilesLoading(false);
        return;
      }

      try {
        setResponseFilesLoading(true);
        setResponseFilesError('');
        const result = await api.get(`/projects/${responseDialogRequest.project_id}/files`);
        const files = result.data?.files || [];
        if (isMounted) {
          setResponseFiles(Array.isArray(files) ? files : []);
        }
      } catch (err) {
        console.error('Load owner response files error:', err);
        if (isMounted) {
          setResponseFiles([]);
          setResponseFilesError(err.response?.data?.error || 'Could not load the photos and documents shared for this response.');
        }
      } finally {
        if (isMounted) {
          setResponseFilesLoading(false);
        }
      }
    };

    loadResponseFiles();

    return () => {
      isMounted = false;
    };
  }, [responseDialogRequest?.id, responseDialogRequest?.project_id]);

  // Load the owner's reference photos when the project detail dialog opens.
  useEffect(() => {
    let isMounted = true;
    const projectId = getProjectId(selectedProject);

    const loadBriefPhotos = async () => {
      if (!projectId) {
        setBriefPhotos([]);
        setBriefDocuments([]);
        return;
      }
      try {
        setBriefPhotosLoading(true);
        const result = await api.get(`/projects/${projectId}/files`, { params: { category: 'photo' } });
        const files = result.data?.files || [];
        if (isMounted) {
          setBriefPhotos(Array.isArray(files) ? files.filter((f) => (f.category || 'photo') === 'photo') : []);
        }
      } catch (err) {
        // Photos are a nice-to-have for pricing — never block the dialog on them.
        if (isMounted) setBriefPhotos([]);
      } finally {
        if (isMounted) setBriefPhotosLoading(false);
      }

      // Owner's plans/drawings (PDFs from the project brief). Separate call so
      // a failure here never hides the photos.
      try {
        const docResult = await api.get(`/projects/${projectId}/files`, { params: { category: 'document' } });
        const docs = docResult.data?.files || [];
        if (isMounted) {
          setBriefDocuments(Array.isArray(docs) ? docs.filter((f) => f.category === 'document') : []);
        }
      } catch (err) {
        if (isMounted) setBriefDocuments([]);
      }
    };

    loadBriefPhotos();
    return () => { isMounted = false; };
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  // Deep link / shareable URL: open the detail dialog for ?project=<id>. Use an
  // already-loaded project if we have it; otherwise fetch it by id. Removing the
  // param (e.g. Close button) closes the dialog.
  useEffect(() => {
    const projectId = searchParams.get('project');
    if (!projectId) {
      if (detailsDialogOpen) setDetailsDialogOpen(false);
      return;
    }
    if (detailsDialogOpen && getProjectId(selectedProject) === projectId) return;

    const found = [projects, recommended, savedProjects]
      .flat()
      .find((p) => getProjectId(p) === projectId);
    if (found) {
      setSelectedProject(found);
      setDetailsDialogOpen(true);
      return;
    }

    let cancelled = false;
    (async () => {
      const res = await marketplaceService.getOpenProject(projectId);
      if (cancelled) return;
      const project = res?.data?.project || res?.project || null;
      if (project) {
        setSelectedProject(project);
        setDetailsDialogOpen(true);
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, projects, recommended, savedProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const tabName = Object.entries(MARKETPLACE_TABS).find(([, value]) => value === newValue)?.[0] || 'recommended';
    setSearchParams(tabName === 'recommended' ? {} : { tab: tabName });
  };

  const handleSortChange = (value) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: value,
      order: value === 'proposal_deadline' ? 'asc' : 'desc'
    }));
  };

  const loadMarketplace = async () => {
    try {
      setLoading(true);
      setError('');
      
      const result = await marketplaceService.getOpenProjects({ ...filters, searchTerm });
      
      if (result.success) {
        setProjects(result.data.projects || result.data || []);
      } else {
        setProjects([]);
        setError('No projects available at the moment. Please try again later.');
      }
    } catch (err) {
      console.error('Load marketplace error:', err);
      setProjects([]);
      setError('Failed to load marketplace projects. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadSavedProjects = async () => {
    try {
      setLoading(true);
      const result = await marketplaceService.getSavedProjects();
      if (result.success) {
        setSavedProjects(result.data.projects || []);
      }
    } catch (err) {
      console.error('Load saved projects error:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMyProposals = async () => {
    try {
      setLoading(true);
      setError('');
      const [proposalResponse, assessmentResponse] = await Promise.allSettled([
        marketplaceService.getMyProposals(),
        marketplaceService.getMyAssessmentRequests()
      ]);

      const proposalResult = proposalResponse.status === 'fulfilled' ? proposalResponse.value : null;
      const assessmentResult = assessmentResponse.status === 'fulfilled' ? assessmentResponse.value : null;
      const proposalList = proposalResult?.data?.proposals || proposalResult?.data?.projectProposals || proposalResult?.proposals || [];
      const assessmentList = assessmentResult?.data?.assessmentRequests || assessmentResult?.data?.assessment_requests || assessmentResult?.assessmentRequests || [];

      if (proposalResult?.success) {
        setMyProposals(Array.isArray(proposalList) ? proposalList : []);
      } else {
        setMyProposals([]);
      }

      if (assessmentResult?.success) {
        setAssessmentRequests(Array.isArray(assessmentList) ? assessmentList : []);
      } else {
        setAssessmentRequests([]);
      }

      if (!proposalResult?.success || !assessmentResult?.success) {
        const failedSections = [
          !proposalResult?.success ? 'quotations' : null,
          !assessmentResult?.success ? 'requests' : null
        ].filter(Boolean).join(' and ');
        const message = `Failed to load your ${failedSections || 'requests and quotations'}.`;
        setError(message);
      }
    } catch (err) {
      console.error('Load proposals error:', err);
      setError(err.response?.data?.message || 'Failed to load your requests and quotations.');
      setMyProposals([]);
      setAssessmentRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const loadRecommended = async () => {
    try {
      setLoading(true);
      const result = await marketplaceService.getRecommendedProjects();
      if (result.success) {
        setRecommended(result.data.projects || []);
      }
    } catch (err) {
      console.error('Load recommended error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProject = async (projectId) => {
    try {
      if (!projectId) {
        setError('Could not save this project because its project ID is missing.');
        setTimeout(() => setError(''), 3000);
        return;
      }

      await marketplaceService.saveProject(projectId);
      setSuccess('Project saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
      loadSavedProjects();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to save project');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleUnsaveProject = async (projectId) => {
    try {
      if (!projectId) {
        setError('Could not unsave this project because its project ID is missing.');
        setTimeout(() => setError(''), 3000);
        return;
      }

      await marketplaceService.unsaveProject(projectId);
      setSavedProjects((prev) => prev.filter((p) => getProjectId(p) !== projectId));
      setSuccess('Project removed from saved list');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || 'Failed to remove saved project');
      setTimeout(() => setError(''), 3000);
    }
  };


  // Reflect the open project in the URL (?project=<id>) so a contractor can share
  // or bookmark a direct link to it, while preserving the current tab param.
  const setProjectParam = (projectId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (projectId) {
        next.set('project', projectId);
      } else {
        next.delete('project');
      }
      return next;
    }, { replace: true });
  };

  // Share an open project into WhatsApp/etc. via the /share/ URL — the
  // preview shows only the coarse title and area, never the owner's address.
  const [shareToastOpen, setShareToastOpen] = useState(false);
  const handleShareProject = async (project) => {
    const projectId = getProjectId(project);
    if (!projectId) return;
    const result = await shareProfileLink({
      path: `/share/project/${projectId}`,
      title: coarseTitle(project?.title || project?.name, project?.location) || 'Project on Eqaya'
    });
    if (result === 'copied') setShareToastOpen(true);
  };

  const handleViewProjectDetails = (project) => {
    setSelectedProject(project);
    setDetailsDialogOpen(true);
    setProjectParam(getProjectId(project));
  };

  const closeProjectDetails = () => {
    setDetailsDialogOpen(false);
    setProjectParam(null);
  };

  const getProjectId = (project) => {
    const projectId = project?.id || project?.project_id || project?.projectId;
    return projectId === null || projectId === undefined ? '' : String(projectId).trim();
  };

  const isProjectSaved = (project) => {
    const id = getProjectId(project);
    if (!id) return false;
    return savedProjects.some((p) => getProjectId(p) === id);
  };

  // The request message is the contractor's first words to this client, so it
  // uses the same introduction template as the chat: name, business,
  // experience, what they need (from the ticked items), one next step.
  const ASSESSMENT_NEED_PHRASES = {
    site_visit: 'a site visit so I can see the property, access and existing condition',
    building_plans: 'the building plans or drawings',
    photos_videos: 'photos or a short video of the site',
    measurements: 'the measurements of the areas involved',
    material_specs: 'the materials and finishes you have in mind',
    boq_schedule: 'your bill of quantities or material schedule, if you have one',
    client_call: 'a short call to go through your priorities, budget and timing',
    verifier_report: 'a verifier or project manager report on the site'
  };
  const buildDefaultAssessmentMessage = (project, items = ['site_visit']) => {
    const needs = (items || []).map((k) => ASSESSMENT_NEED_PHRASES[k]).filter(Boolean);
    return INTRO_TEMPLATES[0].build({ user, clientName: '', projectTitle: project?.title || project?.name || '', needs });
  };

  const findMyAssessmentRequest = (project) => {
    const projectId = getProjectId(project);
    if (!projectId) return null;
    return assessmentRequests.find((r) => String(r.project_id) === String(projectId)) || null;
  };

  const handleOpenAssessmentRequest = (project) => {
    if (!checkAction('bid')) return;
    // A contractor can only have ONE assessment request per project — the create
    // path hard-refuses duplicates. If one already exists, editing it is the only
    // way to add/change details (e.g. site-visit times), so route there instead of
    // the dead-end create form that just errors "already requested".
    const existing = findMyAssessmentRequest(project);
    if (existing) {
      if (canEditAssessmentRequest(existing)) {
        handleEditAssessmentRequest(existing);
      } else {
        setActiveTab(2);
        setSearchParams({ tab: 'requests' });
        setError('You already have an assessment request for this project. Open it under "My Requests" to review its status.');
      }
      return;
    }
    setSelectedProject(project);
    setEditingAssessmentRequest(null);
    setAssessmentError('');
    setAssessmentRequest({
      assessmentItems: ['site_visit'],
      requestMessage: buildDefaultAssessmentMessage(project),
      availabilityNotes: '',
      availabilityWindows: ['', '', ''],
      visitFeeAmount: '',
      visitFeeDescription: ''
    });
    setAssessmentDialog(true);
  };

  // Prefill visit pickers with Harare wall-clock (not the viewer's local zone).
  const toDateTimeInputValue = (dateString) => isoToHarareInput(dateString);

  const handleEditAssessmentRequest = (request) => {
    if (!checkAction('bid')) return;
    const windows = safeArray(request.availability_windows).map(toDateTimeInputValue).filter(Boolean);
    setSelectedProject({
      id: request.project_id,
      title: request.project_title,
      name: request.project_title,
      description: request.project_description,
      location: request.project_location,
      total_budget: request.project_budget,
      currency: request.project_currency || 'USD',
      status: 'open_for_bids'
    });
    setEditingAssessmentRequest(request);
    setAssessmentError('');
    setAssessmentRequest({
      assessmentItems: safeArray(request.assessment_items).length > 0 ? safeArray(request.assessment_items) : ['site_visit'],
      requestMessage: request.request_message || '',
      availabilityNotes: request.availability_notes || '',
      availabilityWindows: [...windows, '', '', ''].slice(0, 3),
      // Once the owner has paid, the amount is whatever they paid: show it, and
      // the server refuses any change to it.
      visitFeeAmount: request.visit_fee_amount ? String(request.visit_fee_amount) : '',
      visitFeeDescription: request.visit_fee_description || ''
    });
    setAssessmentDialog(true);
  };

  const handleOpenAiBid = (project) => {
    if (!checkAction('bid')) return;
    setSelectedProject(project);
    setAiBidDialog(true);
  };

  const handleBuildBidFromAssessment = (request) => {
    setSelectedProject({
      id: request.project_id,
      title: request.project_title,
      name: request.project_title,
      description: request.project_description,
      location: request.project_location,
      total_budget: request.project_budget,
      currency: request.project_currency || 'USD',
      project_type: request.project_type,
      status: 'open_for_bids',
      assessmentRequest: request
    });
    setAiBidDialog(true);
  };

  const canWithdrawAssessmentRequest = (request) =>
    ['submitted', 'owner_responded'].includes(request?.status);

  const canEditAssessmentRequest = (request) =>
    !['ready_for_verified_bid', 'declined', 'withdrawn'].includes(request?.status);

  const canWithdrawProposal = (proposal) =>
    ['submitted', 'pending'].includes(proposal?.status);

  // A scheduled assessment time may be a physical site visit or a client
  // call — label by the visit record's type, never assume "site visit".
  const isCallVisit = (request) => ['client_call', 'video_call'].includes(request?.scheduled_site_visit_type);
  const visitLabelFor = (request) => (isCallVisit(request) ? 'Client call' : 'Site visit');
  const DECLINE_REASON_LABELS = {
    timing: 'not the right time yet',
    other_contractor: 'going with someone else',
    not_going_ahead: 'project on hold',
    quote_from_brief: 'just quote from the brief',
  };

  const formatAssessmentStatus = (request) => ({
    submitted: 'Submitted',
    owner_responded: 'Owner responded',
    details_provided: 'Details provided',
    site_visit_scheduled: `${visitLabelFor(request)} scheduled`,
    ready_for_verified_bid: 'Ready for quotation',
    declined: request?.decline_reason ? `Declined — ${DECLINE_REASON_LABELS[request.decline_reason] || request.decline_reason}` : 'Declined',
    withdrawn: 'Withdrawn',
    expired: 'No answer — closed'
  }[request?.status] || request?.status);

  const hasOwnerResponse = (request) =>
    Boolean(
      request?.owner_response_message ||
      request?.scheduled_site_visit_date ||
      safeArray(request?.owner_provided_items).length > 0 ||
      safeArray(request?.owner_proposed_windows).length > 0
    );

  const isSiteVisitDueForConfirmation = (request) => {
    if (!request?.site_visit_id || !request?.scheduled_site_visit_date) return false;
    if (request?.scheduled_site_visit_status === 'completed') return false;
    return new Date(request.scheduled_site_visit_date).getTime() <= Date.now();
  };

  const canAcceptOwnerWindow = (request) =>
    ['owner_responded', 'details_provided'].includes(request?.status) &&
    !request?.site_visit_id &&
    !request?.scheduled_site_visit_date;

  const handleAcceptOwnerWindow = async (request, windowValue) => {
    const confirmed = window.confirm(`Confirm the site visit for ${formatDateTime(windowValue)}? The project owner will be notified.`);
    if (!confirmed) return;

    try {
      setError('');
      setAcceptingWindow(`${request.id}:${windowValue}`);
      await marketplaceService.acceptAssessmentWindow(request.id, windowValue);
      setSuccess(`Site visit confirmed for ${formatDateTime(windowValue)}. The project owner has been notified.`);
      setResponseDialogRequest(null);
      loadMyProposals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm the site visit.');
    } finally {
      setAcceptingWindow(null);
    }
  };

  const handleMessageOwner = (request) => {
    const params = new URLSearchParams();
    if (request?.project_owner_id) params.set('userId', request.project_owner_id);
    if (request?.project_id) params.set('projectId', request.project_id);
    navigate(`/messages?${params.toString()}`);
  };

  const handleWithdrawAssessmentRequest = async (request) => {
    const confirmed = window.confirm('Withdraw this request? The owner will no longer need to respond to it.');
    if (!confirmed) return;

    try {
      setError('');
      await marketplaceService.withdrawAssessmentRequest(request.id);
      setSuccess('Request withdrawn. The project owner has been notified.');
      loadMyProposals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to withdraw request.');
    }
  };

  const handleWithdrawProposal = async (proposal) => {
    const confirmed = window.confirm('Withdraw this quotation? You will need to build and submit a new one if you want to price this project again.');
    if (!confirmed) return;

    try {
      setError('');
      await marketplaceService.updateProposal(proposal.id, 'withdrawn');
      setSuccess('Quotation withdrawn.');
      loadMyProposals();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to withdraw quotation.');
    }
  };

  const handleCompleteSiteVisit = (request) => {
    setCompleteVisitError('');
    setCompleteVisitPhotos([]);
    const defaultDue = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    setCompleteVisitBidBy(defaultDue.toISOString().slice(0, 10));
    setCompleteVisitRequest(request);
  };

  // One-step reschedule: parties re-agree in chat when reality intervenes —
  // record the new time and notify the owner, no propose/accept round-trip.
  const handleOpenReschedule = (request) => {
    setRescheduleError('');
    setRescheduleTime(isoToHarareInput(request.scheduled_site_visit_date) || '');
    setRescheduleRequest(request);
  };

  const handleSubmitReschedule = async () => {
    if (!rescheduleTime) {
      setRescheduleError('Pick the new visit date and time.');
      return;
    }
    setRescheduling(true);
    setRescheduleError('');
    try {
      const response = await api.put(
        `/projects/${rescheduleRequest.project_id}/assessment-requests/${rescheduleRequest.id}/reschedule-visit`,
        { newTime: harareInputToIso(rescheduleTime) }
      );
      if (response.data.success) {
        setSuccess(response.data.message || 'Site visit rescheduled — the owner has been notified.');
        setRescheduleRequest(null);
        loadMyProposals();
      }
    } catch (err) {
      setRescheduleError(err.response?.data?.message || 'Failed to reschedule the visit.');
    } finally {
      setRescheduling(false);
    }
  };

  const handleCompleteVisitPhotoChange = (event) => {
    const files = Array.from(event.target.files || []).filter((file) => file.type.startsWith('image/'));
    setCompleteVisitPhotos((prev) => [...prev, ...files].slice(0, 5));
    event.target.value = '';
  };

  const handleSubmitCompleteVisit = async () => {
    // Proof photos make sense for a physical visit, not a phone/video call.
    const photosRequired = !isCallVisit(completeVisitRequest);
    if (photosRequired && completeVisitPhotos.length === 0) {
      setCompleteVisitError('Add at least one photo taken at the site — it shows the owner you were there.');
      return;
    }
    if (!completeVisitBidBy) {
      setCompleteVisitError('Tell the owner when you will send your quotation.');
      return;
    }
    try {
      setCompleteVisitError('');
      setCompletingVisit(true);
      const formData = new FormData();
      completeVisitPhotos.forEach((file) => formData.append('photos', file));
      formData.append('bidPromisedBy', new Date(`${completeVisitBidBy}T23:59:59`).toISOString());
      await marketplaceService.completeAssessmentVisit(completeVisitRequest.id, formData);
      setSuccess(`${visitLabelFor(completeVisitRequest)} marked complete — the owner has been notified and knows when to expect your quotation.`);
      setCompleteVisitRequest(null);
      loadMyProposals();
    } catch (err) {
      setCompleteVisitError(err.response?.data?.message || 'Failed to mark it complete.');
    } finally {
      setCompletingVisit(false);
    }
  };

  const toggleAssessmentItem = (value) => {
    setAssessmentRequest((prev) => {
      const exists = prev.assessmentItems.includes(value);
      const nextItems = exists
        ? prev.assessmentItems.filter((item) => item !== value)
        : [...prev.assessmentItems, value];
      // Keep the message in step with the ticked items unless the
      // contractor has already edited it.
      const untouched = prev.requestMessage === buildDefaultAssessmentMessage(selectedProject, prev.assessmentItems);
      return {
        ...prev,
        assessmentItems: nextItems,
        requestMessage: untouched ? buildDefaultAssessmentMessage(selectedProject, nextItems) : prev.requestMessage
      };
    });
  };

  // Availability pickers are Harare (CAT) wall-clock; anchor to CAT → UTC ISO.
  const normalizeDateTimeWindow = (value) => harareInputToIso(value);

  const handleSubmitAssessmentRequest = async () => {
    try {
      setAssessmentError('');
      const projectId = getProjectId(selectedProject);

      if (!projectId) {
        setAssessmentError('Missing project ID. Please close this window, reopen the project details, and try again.');
        return;
      }
      if (assessmentRequest.assessmentItems.length === 0) {
        setAssessmentError('Please choose what you need before you can price the project.');
        return;
      }
      if (assessmentRequest.requestMessage.trim().length < 20) {
        setAssessmentError('Please add a short message explaining what you need.');
        return;
      }

      const payload = {
        projectId,
        assessmentItems: assessmentRequest.assessmentItems,
        requestMessage: assessmentRequest.requestMessage,
        availabilityNotes: assessmentRequest.availabilityNotes,
        availabilityWindows: assessmentRequest.availabilityWindows
          .map(normalizeDateTimeWindow)
          .filter(Boolean),
        visitFeeAmount: assessmentRequest.visitFeeAmount === '' ? null : Number(assessmentRequest.visitFeeAmount),
        visitFeeDescription: assessmentRequest.visitFeeDescription || null
      };

      if (payload.visitFeeAmount !== null
        && (!Number.isFinite(payload.visitFeeAmount) || payload.visitFeeAmount < 1 || payload.visitFeeAmount > 500)) {
        setAssessmentError('A call-out fee must be between $1 and $500, or left empty.');
        return;
      }
      if (payload.visitFeeAmount !== null && payload.availabilityWindows.length === 0) {
        setAssessmentError('Offer at least one visit date before asking for a call-out fee.');
        return;
      }

      if (editingAssessmentRequest?.id) {
        await marketplaceService.updateAssessmentRequest(editingAssessmentRequest.id, payload);
      } else {
        await marketplaceService.submitAssessmentRequest(payload);
      }

      setSuccess(editingAssessmentRequest?.id
        ? 'Assessment request updated. The project owner has been notified about the changes.'
        : 'Assessment request sent. The project owner can now provide plans, photos, measurements, or arrange the assessment before you create a fixed quotation.');
      setAssessmentDialog(false);
      setEditingAssessmentRequest(null);
      localStorage.setItem('eqaya_first_proposal_submitted', 'true');
      setActiveTab(2);
      setSearchParams({ tab: 'requests' });
      loadMyProposals();
    } catch (err) {
      const validationMessages = err.response?.data?.errors
        ?.map((validationError) => {
          const message = validationError.message || validationError.msg;
          return validationError.field && message
            ? `${validationError.field}: ${message}`
            : message;
        })
        .filter(Boolean)
        .join(' ');
      setAssessmentError(validationMessages || err.response?.data?.message || err.response?.data?.error || 'Failed to send assessment request');
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      return 'Budget not listed';
    }

    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(numericAmount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString();
  };

  // Visit times in the property's timezone (Harare / CAT), labelled.
  const formatDateTime = (dateString) => {
    if (!dateString) return 'Not set';
    return formatHarare(dateString) || 'Not set';
  };

  const safeArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch (err) {
        return value.startsWith('{') && value.endsWith('}')
          ? value.slice(1, -1).split(',').filter(Boolean)
          : [];
      }
    }
    return [];
  };

  const getResponseFileUrl = (file) => file?.url || file?.fileUrl || file?.file_url || '';

  const getResponseFileName = (file) => (
    file?.fileName ||
    file?.filename ||
    file?.name ||
    getResponseFileUrl(file).split('/').pop()?.split('?')[0] ||
    'Project file'
  );

  const isResponseImage = (file) => (
    file?.category === 'photo' ||
    file?.fileType?.startsWith?.('image/') ||
    /\.(jpg|jpeg|png|gif|webp)$/i.test(getResponseFileName(file))
  );

  const toMediaViewerItem = (file) => ({
    id: file?.id || getResponseFileUrl(file) || getResponseFileName(file),
    url: getResponseFileUrl(file),
    fileName: getResponseFileName(file),
    category: file?.category || (isResponseImage(file) ? 'photo' : 'document'),
    uploadedAt: file?.uploadedAt || file?.uploaded_at,
    description: file?.description
  });

  const getProjectThumbnailUrl = (project) => (
    project?.project_thumbnail_url ||
    project?.projectThumbnailUrl ||
    project?.thumbnail_url ||
    project?.cover_image_url ||
    ''
  );

  const getProjectPhotoCount = (project) => {
    const rawCount = project?.project_photo_count ?? project?.projectPhotoCount ?? project?.photo_count ?? project?.photoCount;
    const count = Number(rawCount);
    if (Number.isFinite(count) && count > 0) return count;

    return safeArray(project?.photos || project?.photo_urls).length;
  };

  const downloadResponseFile = async (file) => {
    const fileUrl = getResponseFileUrl(file);
    if (!fileUrl) return;

    const filename = getResponseFileName(file);
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = filename;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
  };

  const getProjectTypeLabel = (project) => {
    // Prefer the descriptive type the owner actually picked in the wizard:
    // full_project_type for full projects, task_type for quick tasks. The bare
    // project_type ('quick'/'full') is generic and was previously shown raw.
    const specific = project?.full_project_type || project?.fullProjectType
      || project?.task_type || project?.taskType;
    if (specific && typeof specific === 'string' && specific.trim()) {
      return specific.trim();
    }

    const rawType = project?.category || project?.project_type || project?.projectType;
    if (!rawType || typeof rawType !== 'string') return null;

    const normalized = rawType.trim().toLowerCase();
    if (!normalized || ['full', 'quick', 'other', 'project', 'general', 'custom'].includes(normalized)) {
      return null;
    }

    return PROJECT_TYPE_LABELS[normalized] || rawType.replace(/_/g, ' ');
  };

  const getTimeRemaining = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const end = new Date(deadline);
    if (Number.isNaN(end.getTime())) return null;
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    
    if (days < 0) return 'Expired';
    if (days === 0) return 'Today';
    if (days === 1) return '1 day left';
    return `${days} days left`;
  };

  // When a project was posted: relative for recent, absolute date otherwise.
  const getPostedLabel = (dateString) => {
    if (!dateString) return null;
    const posted = new Date(dateString);
    if (Number.isNaN(posted.getTime())) return null;
    const days = Math.floor((Date.now() - posted.getTime()) / (1000 * 60 * 60 * 24));
    if (days <= 0) return 'Posted today';
    if (days === 1) return 'Posted yesterday';
    if (days < 7) return `Posted ${days} days ago`;
    return `Posted ${posted.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })}`;
  };

  const getProjectReadiness = (project) => {
    const hasDescription = (project.description || project.project_description || '').trim().length >= 60;
    const hasBudget = Number(project.total_budget || project.budget || project.estimated_budget) > 0;
    const hasLocation = !!(project.city || project.location);
    const hasDeadline = !!project.proposal_deadline;
    const attachments = safeArray(project.attachments || project.documents || project.files || project.supporting_documents);
    const hasEvidence = attachments.length > 0 || Number(project.project_photo_count) > 0 || Number(project.project_document_count) > 0 || project.has_photos || project.has_measurements || project.has_drawings || project.has_boq;
    // Structured questionnaire answers (build stage, plans, rooms, finishes,
    // materials, measurements…) count as readiness too — mirrors the checklist
    // the owner saw in the creation wizard.
    const hasDetails = answeredBriefCount(parseStructuredBrief(project.structured_brief)) >= 2;
    const score = [hasDescription, hasBudget, hasLocation, hasDeadline, hasEvidence, hasDetails].filter(Boolean).length;

    if (score >= 4) {
      return {
        key: 'ready',
        label: 'Detailed Brief',
        color: 'success',
        icon: <CheckCircle fontSize="small" />,
        helper: 'A good brief — still confirm the site, plans, measurements or a call with the owner before you price the work.'
      };
    }

    if (score >= 2) {
      return {
        key: 'assessment',
        label: 'Assessment Recommended',
        color: 'warning',
        icon: <FactCheck fontSize="small" />,
        helper: 'Ask the owner for a site visit, building plans, photos, measurements, BOQ, material specs, or a call before you price the work.'
      };
    }

    return {
      key: 'needs_details',
      label: 'Needs More Details',
      color: 'default',
      icon: <Warning fontSize="small" />,
      helper: 'Ask the owner for a site visit, building plans, photos, measurements, BOQ, material specs, or a call before you price the work.'
    };
  };

  const ProjectCard = ({ project, showActions = true }) => {
    const title = coarseTitle(project.title || project.name, project.location) || 'Untitled Project';
    const description = project.description || project.project_description || 'No project description provided yet.';
    const readiness = getProjectReadiness(project);
    const myRequest = findMyAssessmentRequest(project);
    // A quotation is only ever built from enough detail: a detailed brief, or
    // a details request already sent (the owner may have answered it).
    const canQuote = readiness.key === 'ready' || Boolean(myRequest);
    const thumbnailUrl = getProjectThumbnailUrl(project);
    const photoCount = getProjectPhotoCount(project);
    const timeRemaining = getTimeRemaining(project.proposal_deadline);
    const postedLabel = getPostedLabel(project.created_at || project.createdAt);

    return (
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden'
        }}
      >
        <Box
          onClick={() => handleViewProjectDetails(project)}
          sx={{
            position: 'relative',
            width: { xs: '100%', md: 240 },
            minHeight: { xs: 180, md: 252 },
            flexShrink: 0,
            cursor: 'pointer',
            bgcolor: 'rgba(13, 148, 136, 0.08)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {thumbnailUrl ? (
            <Box
              component="img"
              src={thumbnailUrl}
              alt={`${title} photo`}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Box sx={{ textAlign: 'center', color: 'text.secondary', px: 2 }}>
              <Image sx={{ fontSize: 44, mb: 1, color: 'primary.main' }} />
              <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                Photos not added yet
              </Typography>
            </Box>
          )}
          {photoCount > 0 && (
            <Chip
              label={`${photoCount} ${photoCount === 1 ? 'photo' : 'photos'}`}
              size="small"
              sx={{
                position: 'absolute',
                left: 12,
                bottom: 12,
                bgcolor: 'rgba(15, 23, 42, 0.82)',
                color: 'common.white',
                fontWeight: 700
              }}
            />
          )}
        </Box>

        <CardContent
          onClick={() => handleViewProjectDetails(project)}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            '&:hover .project-title': { color: 'primary.main' }
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                icon={readiness.icon}
                label={readiness.label}
                color={readiness.color}
                variant={readiness.key === 'ready' ? 'filled' : 'outlined'}
              />
              {parseStructuredBrief(project.structured_brief)?.fundingPreference === 'stages' && (
                <Chip size="small" label="Client pays in stages" color="success" variant="outlined" />
              )}
            </Box>
            {showActions && (() => {
              const saved = isProjectSaved(project);
              return (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    const id = getProjectId(project);
                    if (saved) {
                      handleUnsaveProject(id);
                    } else {
                      handleSaveProject(id);
                    }
                  }}
                  color="primary"
                  aria-label={saved ? 'Remove from saved' : 'Save project'}
                  title={saved ? 'Remove from saved' : 'Save project'}
                >
                  {saved ? <Bookmark /> : <BookmarkBorder />}
                </IconButton>
              );
            })()}
          </Box>

          <Typography className="project-title" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
            {title}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: { xs: 4, md: 3 },
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.6
            }}
          >
            {description}
          </Typography>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {getProjectTypeLabel(project) && (
              <Chip
                icon={<Category />}
                label={getProjectTypeLabel(project)}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
            <Chip
              icon={<LocationOn />}
              label={coarseLocation(project.city || project.location) || 'Location not listed'}
              size="small"
            />
            {project.covers_travel && (
              <Chip size="small" color="success" variant="outlined" label="Client covers travel" />
            )}
          </Box>

          <Box sx={{ mt: 'auto', display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Budget
              </Typography>
              <Typography variant="h6" color="success.main" sx={{ lineHeight: 1.2 }}>
                {formatCurrency(project.total_budget || project.budget || project.estimated_budget, project.currency || 'USD')}
              </Typography>
            </Box>
            {timeRemaining && (
              <Box
                sx={{
                  px: 1,
                  py: 0.75,
                  backgroundColor: 'warning.light',
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75
                }}
              >
                <AccessTime fontSize="small" />
                <Typography variant="caption">
                  {timeRemaining}
                </Typography>
              </Box>
            )}
            {postedLabel && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
              >
                {postedLabel}
              </Typography>
            )}
          </Box>
        </CardContent>

        {showActions && (
          <CardActions
            sx={{
              width: { xs: '100%', md: 260 },
              // Lock the column width so it never gets squeezed by a longer
              // description — otherwise the button wraps to 1/2/3 lines per card.
              flexShrink: 0,
              p: 2,
              gap: 1,
              flexDirection: 'column',
              alignItems: 'stretch',
              justifyContent: 'flex-end',
              borderTop: { xs: '1px solid', md: 0 },
              borderLeft: { xs: 0, md: '1px solid' },
              borderColor: 'divider'
            }}
          >
            {/* A listing is never priced off the card alone: the lead action is
                always to ask the owner for what's missing (which also opens the
                chat with them). Once a request exists, the card offers the chat
                directly; the priced quotation stays one click away. */}
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {myRequest
                ? 'You have asked the owner for details. Message them or build your quotation when you have what you need.'
                : readiness.helper}
            </Typography>
            {myRequest ? (
              <Button
                fullWidth
                variant="contained"
                startIcon={<ChatBubbleOutline />}
                onClick={() => handleMessageOwner(myRequest)}
                sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)', whiteSpace: 'nowrap' }}
              >
                Message Owner
              </Button>
            ) : (
              <Button
                fullWidth
                variant="contained"
                startIcon={<FactCheck />}
                onClick={() => handleOpenAssessmentRequest(project)}
                sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)', whiteSpace: 'nowrap' }}
              >
                Request Details First
              </Button>
            )}
            {canQuote && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AutoAwesome />}
                onClick={() => handleOpenAiBid(project)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Build Quotation
              </Button>
            )}
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Visibility />}
              onClick={() => handleViewProjectDetails(project)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              View Details
            </Button>
          </CardActions>
        )}
      </Card>
    );
  };

  return (
    <>
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Project Marketplace
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Review projects, request missing site details, or submit a verified quotation when you are ready to price the work.
        </Typography>
      </Box>

      {/* Profile incomplete banner */}
      {!profileComplete && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(hasVerifiablePortfolio ? '/profile' : '/contractor/portfolio')}
              sx={{ fontWeight: 600 }}
            >
              {hasVerifiablePortfolio ? 'Complete Profile' : 'Add Portfolio'}
            </Button>
          }
        >
          Finish these requirements before bidding: {(profileData?.missingRequirements || profileData?.missingItems || ['Contractor profile', 'Verifiable portfolio project']).slice(0, 4).join(', ')}.
        </Alert>
      )}

      {/* C4: First-timer marketplace guidance */}
      {myProposals.length === 0 && !user?.profile?.browsedProjects && user?.profile?.browsed_projects !== true && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          onClose={() => {
            localStorage.setItem('eqaya_marketplace_intro_seen', 'true');
            // Force re-render by using a dummy state — the alert just disappears
            setSuccess('');
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Welcome to the Job Marketplace!
          </Typography>
          <Typography variant="body2">
            Click a project to understand it first. Use Request Assessment when you need plans, photos, measurements, or a site visit before pricing.
          </Typography>
        </Alert>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Recommended for You" value={3} icon={<Stars />} iconPosition="end" />
          <Tab label="All Projects" value={0} />
          <Tab label="Saved Projects" value={1} />
          <Tab label="My Requests & Quotes" value={2} />
        </Tabs>
      </Box>

      {/* Search and Filters */}
      {activeTab === 0 && (
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filters.category}
                  label="Category"
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="residential">Residential</MenuItem>
                  <MenuItem value="commercial">Commercial</MenuItem>
                  <MenuItem value="renovation">Renovation</MenuItem>
                  <MenuItem value="infrastructure">Infrastructure</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Min Budget"
                type="number"
                value={filters.minBudget}
                onChange={(e) => setFilters({ ...filters, minBudget: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <TextField
                fullWidth
                label="Max Budget"
                type="number"
                value={filters.maxBudget}
                onChange={(e) => setFilters({ ...filters, maxBudget: e.target.value })}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={filters.sortBy}
                  label="Sort By"
                  onChange={(e) => handleSortChange(e.target.value)}
                >
                  <MenuItem value="created_at">Newest First</MenuItem>
                  <MenuItem value="total_budget">Highest Budget</MenuItem>
                  <MenuItem value="proposal_deadline">Deadline</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                placeholder="Filter by location..."
                value={filters.location}
                onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocationOn />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!filters.coversTravel}
                    onChange={(e) => setFilters({ ...filters, coversTravel: e.target.checked })}
                  />
                }
                label="Covers travel"
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Loading State */}
      {loading && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading projects...
          </Typography>
        </Box>
      )}

      {/* Projects Grid */}
      {!loading && activeTab === 0 && (
        <>
          {projects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Warning sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No projects available
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Check back later for new opportunities
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {projects.map(project => (
                <Grid item xs={12} key={getProjectId(project)}>
                  <ProjectCard project={project} />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      {/* Saved Projects Tab */}
      {!loading && activeTab === 1 && (
        <Grid container spacing={3}>
          {savedProjects.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <BookmarkBorder sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No saved projects yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Save projects to review them later
                </Typography>
              </Box>
            </Grid>
          ) : (
            savedProjects.map(project => (
              <Grid item xs={12} key={getProjectId(project)}>
                <ProjectCard project={project} />
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* My Requests & Quotes Tab */}
      {!loading && activeTab === 2 && (
        <Box>
          {myProposals.length === 0 && assessmentRequests.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Send sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                No requests or quotations yet
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Request missing details first, or build a quotation when you are ready to price the work.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {assessmentRequests.map(request => (
                <Grid item xs={12} key={request.id}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={5}>
                          <Typography variant="h6">{request.project_title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {request.request_message}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                          <Typography variant="caption" color="text.secondary">
                            Requested Details
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                            {safeArray(request.assessment_items).map((item) => (
                              <Chip
                                key={item}
                                size="small"
                                label={ASSESSMENT_OPTIONS.find((option) => option.value === item)?.label || item}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography variant="caption" color="text.secondary">
                            Type
                          </Typography>
                          <Typography variant="body1">Assessment request</Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            Status
                          </Typography>
                          <Chip
                            label={formatAssessmentStatus(request)}
                            color={hasOwnerResponse(request) ? 'success' : 'info'}
                            size="small"
                            sx={{ mt: 0.5, maxWidth: '100%' }}
                          />
                        </Grid>
                        {hasOwnerResponse(request) && (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                border: '1px solid',
                                borderColor: 'success.light',
                                bgcolor: 'success.50',
                                borderRadius: 1,
                                p: 2
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.dark' }}>
                                  Owner response
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Visibility />}
                                  onClick={() => setResponseDialogRequest(request)}
                                >
                                  Review Owner Response
                                </Button>
                              </Box>
                              {request.owner_response_message && (
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  {request.owner_response_message}
                                </Typography>
                              )}
                              {request.scheduled_site_visit_date && (
                                <>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                                      {request.scheduled_site_visit_status === 'completed'
                                        ? `${visitLabelFor(request)} completed: ${formatDateTime(request.scheduled_site_visit_date)}`
                                        : `${visitLabelFor(request)} scheduled: ${formatDateTime(request.scheduled_site_visit_date)}`}
                                    </Typography>
                                    {request.scheduled_site_visit_status !== 'completed' && (
                                      <Button size="small" variant="text" onClick={() => handleOpenReschedule(request)}>
                                        Reschedule
                                      </Button>
                                    )}
                                    {Number(request.scheduled_site_visit_rescheduled_count) > 0 && (
                                      <Typography variant="caption" color="text.secondary">
                                        (rescheduled {request.scheduled_site_visit_rescheduled_count}×)
                                      </Typography>
                                    )}
                                  </Box>
                                  {/* A confirmed visit unlocks the FULL property address (browse
                                      cards only ever show the coarse suburb) — the contractor
                                      needs the doorstep, not a chat back-and-forth. */}
                                  {request.project_location && (
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      <strong>Property address:</strong> {request.project_location}
                                      {!isCallVisit(request) && request.project_latitude && request.project_longitude && (
                                        <>
                                          {' — '}
                                          <a
                                            href={`https://www.google.com/maps/dir/?api=1&destination=${request.project_latitude},${request.project_longitude}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            Get directions
                                          </a>
                                        </>
                                      )}
                                    </Typography>
                                  )}
                                </>
                              )}
                              {request.bid_submitted_at ? (
                                <Typography variant="body2" sx={{ color: 'success.dark', mt: 0.5 }}>
                                  Quotation submitted ✓
                                </Typography>
                              ) : request.bid_promised_by && (
                                new Date(request.bid_promised_by).getTime() < Date.now() ? (
                                  <Typography variant="body2" sx={{ color: 'error.main', fontWeight: 700, mt: 0.5 }}>
                                    Your quotation was due by {new Date(request.bid_promised_by).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} — send it now or message the owner for more time.
                                  </Typography>
                                ) : (
                                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    You promised your quotation by {new Date(request.bid_promised_by).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}.
                                  </Typography>
                                )
                              )}
                              {isSiteVisitDueForConfirmation(request) && (
                                <Alert
                                  severity="warning"
                                  sx={{ mt: 1 }}
                                  action={
                                    <>
                                      <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => handleCompleteSiteVisit(request)}
                                      >
                                        Mark Complete
                                      </Button>
                                      <Button
                                        color="inherit"
                                        size="small"
                                        onClick={() => handleOpenReschedule(request)}
                                      >
                                        Reschedule
                                      </Button>
                                    </>
                                  }
                                >
                                  The scheduled {visitLabelFor(request).toLowerCase()} time has passed. Did it happen? Mark it complete if it did — or reschedule if it was moved.
                                </Alert>
                              )}
                              {safeArray(request.owner_proposed_windows).length > 0 && !request.scheduled_site_visit_date && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Owner proposed alternative times
                                  </Typography>
                                  {canAcceptOwnerWindow(request) ? (
                                    <>
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                                        {safeArray(request.owner_proposed_windows).map((windowValue) => (
                                          <Box key={windowValue} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                            <Chip size="small" label={formatDateTime(windowValue)} />
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="success"
                                              startIcon={<EventAvailable />}
                                              disabled={Boolean(acceptingWindow)}
                                              onClick={() => handleAcceptOwnerWindow(request, windowValue)}
                                            >
                                              {acceptingWindow === `${request.id}:${windowValue}` ? 'Confirming…' : 'Accept this time'}
                                            </Button>
                                          </Box>
                                        ))}
                                      </Box>
                                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Accepting a time confirms the site visit instantly. If none work, use Suggest Different Times below, or message the owner.
                                      </Typography>
                                    </>
                                  ) : (
                                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                      {safeArray(request.owner_proposed_windows).map((windowValue) => (
                                        <Chip key={windowValue} size="small" label={formatDateTime(windowValue)} />
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Grid>
                        )}
                        {hasOwnerResponse(request) && safeArray(request.owner_provided_items).length > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="caption" color="text.secondary">
                              Details owner marked as provided
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                              {safeArray(request.owner_provided_items).map((item) => (
                                <Chip
                                  key={item}
                                  size="small"
                                  color="success"
                                  label={ASSESSMENT_OPTIONS.find((option) => option.value === item)?.label || item}
                                  variant="outlined"
                                />
                              ))}
                            </Box>
                          </Grid>
                        )}
                        {hasOwnerResponse(request) && safeArray(request.owner_provided_items).length === 0 && !request.owner_response_message && !request.scheduled_site_visit_date && (
                          <Grid item xs={12}>
                            <Alert severity="info">
                              The owner responded, but did not mark any requested details as provided yet.
                            </Alert>
                          </Grid>
                        )}
                        {(canEditAssessmentRequest(request) || canWithdrawAssessmentRequest(request) || request.project_owner_id) && (
                          <Grid item xs={12}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {canEditAssessmentRequest(request) && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<Edit />}
                                  onClick={() => handleEditAssessmentRequest(request)}
                                >
                                  {canAcceptOwnerWindow(request) && safeArray(request.owner_proposed_windows).length > 0
                                    ? 'Suggest Different Times'
                                    : 'Edit Request'}
                                </Button>
                              )}
                              {request.project_owner_id && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<ChatBubbleOutline />}
                                  onClick={() => handleMessageOwner(request)}
                                >
                                  Message Owner
                                </Button>
                              )}
                              {canWithdrawAssessmentRequest(request) && (
                                <Button
                                  size="small"
                                  color="error"
                                  variant="outlined"
                                  startIcon={<DeleteOutline />}
                                  onClick={() => handleWithdrawAssessmentRequest(request)}
                                >
                                  Withdraw Request
                                </Button>
                              )}
                            </Box>
                          </Grid>
                        )}
                        {['owner_responded', 'details_provided', 'site_visit_scheduled', 'ready_for_verified_bid'].includes(request.status) && (
                          <Grid item xs={12}>
                            <Alert
                              severity="success"
                              action={
                                <Button
                                  color="inherit"
                                  size="small"
                                  startIcon={<AutoAwesome />}
                                  onClick={() => handleBuildBidFromAssessment(request)}
                                >
                                  Build Quotation Now
                                </Button>
                              }
                            >
                              The project owner responded. Review the details, then build a professional quotation when you can price the work confidently.
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {myProposals.map(proposal => (
                <Grid item xs={12} key={proposal.id}>
                  <Card>
                    <CardContent>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="h6">{proposal.project_title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {proposal.project_description}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography variant="caption" color="text.secondary">
                            Your Quote
                          </Typography>
                          <Typography variant="h6" color="success.main">
                            {formatCurrency(proposal.quote_amount)}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Typography variant="caption" color="text.secondary">
                            Timeline
                          </Typography>
                          <Typography variant="body1">
                            {proposal.timeline_days} days
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={2}>
                          <Chip 
                            label={proposal.status}
                            color={
                              proposal.status === 'accepted' ? 'success' :
                              proposal.status === 'rejected' ? 'error' :
                              'warning'
                            }
                          />
                        </Grid>
                      </Grid>
                    </CardContent>
                    {canWithdrawProposal(proposal) && (
                      <CardActions sx={{ px: 2, pt: 0 }}>
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          startIcon={<DeleteOutline />}
                          onClick={() => handleWithdrawProposal(proposal)}
                        >
                          Withdraw Quotation
                        </Button>
                      </CardActions>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Recommended Tab */}
      {!loading && activeTab === 3 && (
        <Grid container spacing={3}>
          {recommended.length === 0 ? (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Stars sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No recommendations yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Complete your profile to get personalized project recommendations
                </Typography>
                <Button
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/profile')}
                >
                  Complete Profile
                </Button>
              </Box>
            </Grid>
          ) : (
            recommended.map(project => (
              <Grid item xs={12} key={getProjectId(project)}>
                <ProjectCard project={project} />
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Project Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={closeProjectDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={closeProjectDetails} />
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 5 }}>
            <Box sx={{ flex: 1 }}>
              {coarseTitle(selectedProject?.title || selectedProject?.name, selectedProject?.location) || 'Project Details'}
            </Box>
            <IconButton size="small" onClick={() => handleShareProject(selectedProject)} title="Share project link">
              <ShareIcon fontSize="small" />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Review the project before deciding whether to quote.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedProject && (
            <Grid container spacing={3}>
              {(() => {
                const readiness = getProjectReadiness(selectedProject);
                return (
                  <Grid item xs={12}>
                    <Alert severity={readiness.key === 'ready' ? 'success' : 'warning'} icon={readiness.icon}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {readiness.label}
                      </Typography>
                      <Typography variant="body2">
                        {readiness.helper}
                      </Typography>
                    </Alert>
                  </Grid>
                );
              })()}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedProject.description || selectedProject.project_description || 'No description provided.'}
                </Typography>
              </Grid>

              {(() => {
                // Structured brief spec sheet — the owner's questionnaire
                // answers (build stage, plans, rooms, finishes, materials,
                // measurements, timeline, priorities).
                const rows = structuredBriefRows(selectedProject.structured_brief);
                if (rows.length === 0) return null;
                return (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Project Details (from the owner)
                    </Typography>
                    <Grid container spacing={1}>
                      {rows.map((row) => (
                        <Grid item xs={12} sm={6} key={row.label}>
                          <Box sx={{ p: 1.25, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {row.label}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.value}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Grid>
                );
              })()}

              {briefDocuments.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Plans & Documents ({briefDocuments.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    {briefDocuments.map((doc) => (
                      <Button
                        key={doc.id || doc.url}
                        component="a"
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="outlined"
                        size="small"
                        startIcon={<DescriptionIcon />}
                        sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                      >
                        {doc.fileName || 'Plan document'}
                      </Button>
                    ))}
                  </Box>
                </Grid>
              )}

              {(() => {
                // AI Project Insights panel HIDDEN for now: the auto budget and the
                // photo-derived "site condition"/"detected features" were misleading
                // — owners often upload inspiration photos of the desired RESULT,
                // which the model described as the CURRENT site. Hidden retroactively
                // for all projects (simply stops rendering stored ai_metadata).
                // Re-enable by removing this early return.
                const SHOW_AI_INSIGHTS = false;
                if (!SHOW_AI_INSIGHTS) return null;
                // AI insights captured during project creation — helps calibrate bids
                let ai = selectedProject.ai_metadata;
                if (typeof ai === 'string') {
                  try { ai = JSON.parse(ai); } catch { ai = null; }
                }
                if (!ai || (!ai.budgetEstimation && !ai.timelinePrediction && !ai.photoAnalysis)) return null;
                return (
                  <Grid item xs={12}>
                    <Alert severity="info" icon={false} sx={{ bgcolor: 'rgba(13, 148, 136, 0.06)' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        AI Project Insights
                      </Typography>
                      {ai.budgetEstimation?.budgetRange && (
                        <Typography variant="body2">
                          Estimated budget: ${Number(ai.budgetEstimation.budgetRange.min).toLocaleString()} – ${Number(ai.budgetEstimation.budgetRange.max).toLocaleString()} {ai.budgetEstimation.budgetRange.currency || 'USD'}
                        </Typography>
                      )}
                      {(ai.timelinePrediction?.timelineRange || ai.timelinePrediction?.estimatedDuration) && (() => {
                        const range = ai.timelinePrediction.timelineRange || ai.timelinePrediction.estimatedDuration;
                        return (
                          <Typography variant="body2">
                            Estimated timeline: {range.min}–{range.max} {range.unit || 'days'}
                          </Typography>
                        );
                      })()}
                      {ai.photoAnalysis?.conditionAssessment && (
                        <Typography variant="body2">
                          Site condition (from photos): {ai.photoAnalysis.conditionAssessment}
                          {ai.photoAnalysis.complexityLevel ? ` — complexity: ${ai.photoAnalysis.complexityLevel}` : ''}
                        </Typography>
                      )}
                      {Array.isArray(ai.photoAnalysis?.detectedFeatures) && ai.photoAnalysis.detectedFeatures.length > 0 && (
                        <Typography variant="body2">
                          Detected: {ai.photoAnalysis.detectedFeatures.join(', ')}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Generated automatically from the owner's brief and photos — verify on assessment.
                      </Typography>
                    </Alert>
                  </Grid>
                );
              })()}

              {(briefPhotosLoading || briefPhotos.length > 0) && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reference Photos {briefPhotos.length > 0 ? `(${briefPhotos.length})` : ''}
                  </Typography>
                  {briefPhotosLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <Grid container spacing={1.5}>
                      {briefPhotos.map((photo) => (
                        <Grid item xs={6} sm={4} md={3} key={photo.id || photo.url}>
                          <Box
                            onClick={() => setSelectedBriefPhoto(photo)}
                            sx={{ display: 'block', cursor: 'pointer', borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', aspectRatio: '1 / 1', '&:hover': { borderColor: 'primary.main' } }}
                          >
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.fileName || 'Reference photo'}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              )}

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Budget</Typography>
                <Typography variant="h6" color="success.main">
                  {formatCurrency(
                    selectedProject.total_budget || selectedProject.budget || selectedProject.estimated_budget,
                    selectedProject.currency || 'USD'
                  )}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Location</Typography>
                <Typography variant="body1">
                  {coarseLocation(selectedProject.city || selectedProject.location) || 'Not listed'}
                </Typography>
                {selectedProject.covers_travel && (
                  <Chip size="small" color="success" variant="outlined" label="Client covers travel" sx={{ mt: 0.5 }} />
                )}
                {selectedProject.covers_travel && selectedProject.travel_notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Travel: {selectedProject.travel_notes}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Quotation Deadline</Typography>
                <Typography variant="body1">
                  {formatDate(selectedProject.proposal_deadline)}
                </Typography>
              </Grid>

              {getProjectTypeLabel(selectedProject) && (
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="caption" color="text.secondary">Work Category</Typography>
                  <Typography variant="body1">
                    {getProjectTypeLabel(selectedProject)}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="caption" color="text.secondary">Posted</Typography>
                <Typography variant="body1">{formatDate(selectedProject.created_at)}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  Next Step
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose after reviewing the project. If anything important is missing, request the details first instead of guessing a price.
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <FactCheck color="primary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Request Assessment or Project Details
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Ask the owner for a site visit, building plans, photos, measurements, BOQ, material specs, or a call before you price the work.
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<FactCheck />}
                          sx={{ background: 'linear-gradient(135deg, #0d9488 0%, #134e4a 100%)' }}
                          onClick={() => {
                            const project = selectedProject;
                            closeProjectDetails();
                            if (project) handleOpenAssessmentRequest(project);
                          }}
                        >
                          Request Details First
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                  {(getProjectReadiness(selectedProject).key === 'ready' || findMyAssessmentRequest(selectedProject)) && (
                  <Grid item xs={12} md={6}>
                    <Card
                      variant="outlined"
                      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <AutoAwesome color="primary" />
                          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                            Build Verified Quotation
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          Create a professional priced quotation when you already have enough evidence to confirm scope, quantities, materials, timeline, assumptions, and exclusions.
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ px: 2, pb: 2 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<AutoAwesome />}
                          onClick={() => {
                            const project = selectedProject;
                            closeProjectDetails();
                            if (project) handleOpenAiBid(project);
                          }}
                        >
                          Build Quotation
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closeProjectDetails}>
            Close
          </Button>
          {selectedProject && isProjectSaved(selectedProject) ? (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Bookmark />}
              onClick={() => handleUnsaveProject(getProjectId(selectedProject))}
            >
              Remove from Saved
            </Button>
          ) : (
            <Button
              variant="outlined"
              startIcon={<BookmarkBorder />}
              onClick={() => selectedProject && handleSaveProject(getProjectId(selectedProject))}
            >
              Save Project
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Assessment Request Dialog */}
      <Dialog
        open={assessmentDialog}
        onClose={() => {
          setAssessmentDialog(false);
          setAssessmentError('');
          setEditingAssessmentRequest(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => {
          setAssessmentDialog(false);
          setEditingAssessmentRequest(null);
        }} />
        <DialogTitle>
          {editingAssessmentRequest ? 'Edit Assessment Request' : 'Request Assessment or Project Details'}
          {selectedProject && (
            <Typography variant="body2" color="text.secondary">
              Ask for the site visit, plans, photos, measurements, or documents you need before giving a fixed price.
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {assessmentError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssessmentError('')}>
              {assessmentError}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 3 }}>
            Use this when the project looks interesting but the owner has not provided enough information to price it responsibly. This is not a quotation and does not include a price.
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            What do you need before pricing?
          </Typography>
          <Grid container spacing={1.5} sx={{ mb: 3 }}>
            {ASSESSMENT_OPTIONS.map((option) => (
              <Grid item xs={12} sm={6} key={option.value}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: assessmentRequest.assessmentItems.includes(option.value) ? 'primary.main' : 'divider',
                    borderRadius: 1,
                    p: 1.25,
                    height: '100%'
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={assessmentRequest.assessmentItems.includes(option.value)}
                        onChange={() => toggleAssessmentItem(option.value)}
                      />
                    }
                    label={option.label}
                    sx={{ alignItems: 'flex-start', mr: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                    {option.helper}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                multiline
                minRows={8}
                maxRows={14}
                label="Message to Project Owner"
                value={assessmentRequest.requestMessage}
                onChange={(e) => setAssessmentRequest({ ...assessmentRequest, requestMessage: e.target.value })}
                helperText="Explain clearly what you need and why it is required before a fixed quote."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Review Notes"
                placeholder="Example: I can review plans/photos within 24 hours, or I need the client to confirm access before a visit."
                value={assessmentRequest.availabilityNotes}
                onChange={(e) => setAssessmentRequest({ ...assessmentRequest, availabilityNotes: e.target.value })}
              />
            </Grid>
            {assessmentRequest.assessmentItems.includes('site_visit') && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Suggested Site Visit Windows
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  Offer up to three date/time options, in Zimbabwe (Harare / CAT) time. The project owner can accept one or propose alternatives.
                </Typography>
                <Grid container spacing={1.5}>
                  {assessmentRequest.availabilityWindows.map((windowValue, index) => (
                    <Grid item xs={12} sm={4} key={index}>
                      <TextField
                        fullWidth
                        type="datetime-local"
                        label={`Option ${index + 1}`}
                        value={windowValue}
                        onChange={(e) => {
                          const nextWindows = [...assessmentRequest.availabilityWindows];
                          nextWindows[index] = e.target.value;
                          setAssessmentRequest({ ...assessmentRequest, availabilityWindows: nextWindows });
                        }}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  ))}
                </Grid>

                {/* Travel money for a long trip. Optional, and only ever the
                    contractor's own figure — Eqaya never suggests an amount. */}
                {assessmentRequest.availabilityWindows.some((w) => w) && (
                  <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      Call-out fee (optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      For long trips only. The owner pays it through Eqaya before you travel; it is
                      held and paid into your earnings after the visit. Never collect cash on the day.
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Amount (USD)"
                          value={assessmentRequest.visitFeeAmount}
                          onChange={(e) => setAssessmentRequest({ ...assessmentRequest, visitFeeAmount: e.target.value })}
                          inputProps={{ min: 1, max: 500, step: 1 }}
                          helperText="$1–$500, or leave empty"
                        />
                      </Grid>
                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="What it covers"
                          placeholder="e.g. fuel, Harare to Shurugwi return"
                          value={assessmentRequest.visitFeeDescription}
                          onChange={(e) => setAssessmentRequest({ ...assessmentRequest, visitFeeDescription: e.target.value })}
                          inputProps={{ maxLength: 200 }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAssessmentDialog(false);
            setAssessmentError('');
            setEditingAssessmentRequest(null);
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitAssessmentRequest}
            startIcon={<FactCheck />}
          >
            {editingAssessmentRequest ? 'Update Request' : 'Send Assessment Request'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(responseDialogRequest)}
        onClose={() => setResponseDialogRequest(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogCloseButton onClick={() => setResponseDialogRequest(null)} />
        <DialogTitle>
          Owner Response
          <Typography variant="body2" color="text.secondary">
            {responseDialogRequest?.project_title}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {responseDialogRequest?.owner_response_message ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              {responseDialogRequest.owner_response_message}
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              The owner responded without adding a written message.
            </Alert>
          )}

          {responseDialogRequest?.scheduled_site_visit_date && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Site Visit
              </Typography>
              <Typography variant="body2">
                {responseDialogRequest.scheduled_site_visit_status === 'completed'
                  ? `Completed for ${formatDateTime(responseDialogRequest.scheduled_site_visit_date)}`
                  : `Scheduled for ${formatDateTime(responseDialogRequest.scheduled_site_visit_date)}`}
              </Typography>
              {isSiteVisitDueForConfirmation(responseDialogRequest) && (
                <Alert
                  severity="warning"
                  sx={{ mt: 1 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => handleCompleteSiteVisit(responseDialogRequest)}
                    >
                      Mark Complete
                    </Button>
                  }
                >
                  The scheduled time has passed. Confirm completion before treating the visit as done.
                </Alert>
              )}
            </Box>
          )}

          {safeArray(responseDialogRequest?.owner_provided_items).length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Details Marked as Provided
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                {safeArray(responseDialogRequest.owner_provided_items).map((item) => (
                  <Chip
                    key={item}
                    size="small"
                    color="success"
                    label={ASSESSMENT_OPTIONS.find((option) => option.value === item)?.label || item}
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}

          {safeArray(responseDialogRequest?.owner_proposed_windows).length > 0 && !responseDialogRequest?.scheduled_site_visit_date && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                Alternative Visit Times Proposed by Owner
              </Typography>
              {canAcceptOwnerWindow(responseDialogRequest) ? (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {safeArray(responseDialogRequest.owner_proposed_windows).map((windowValue) => (
                      <Box key={windowValue} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Chip size="small" label={formatDateTime(windowValue)} />
                        <Button
                          size="small"
                          variant="contained"
                          color="success"
                          startIcon={<EventAvailable />}
                          disabled={Boolean(acceptingWindow)}
                          onClick={() => handleAcceptOwnerWindow(responseDialogRequest, windowValue)}
                        >
                          {acceptingWindow === `${responseDialogRequest.id}:${windowValue}` ? 'Confirming…' : 'Accept this time'}
                        </Button>
                      </Box>
                    ))}
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                    Accepting a time confirms the site visit instantly and notifies the owner.
                  </Typography>
                </>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {safeArray(responseDialogRequest.owner_proposed_windows).map((windowValue) => (
                    <Chip key={windowValue} size="small" label={formatDateTime(windowValue)} />
                  ))}
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              Photos & Documents Shared by Owner
            </Typography>

            {responseFilesLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Loading shared files...
                </Typography>
              </Box>
            )}

            {!responseFilesLoading && responseFilesError && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                {responseFilesError}
              </Alert>
            )}

            {!responseFilesLoading && !responseFilesError && responseFiles.length === 0 && (
              <Alert severity="info">
                No photos or documents are attached to the project yet.
              </Alert>
            )}

            {!responseFilesLoading && responseFiles.length > 0 && (
              (() => {
                const photoFiles = responseFiles.filter(isResponseImage);
                const documentFiles = responseFiles.filter((file) => !isResponseImage(file));
                return (
                  <Box>
                    {photoFiles.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {photoFiles.length} photo{photoFiles.length === 1 ? '' : 's'}
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Download />}
                            onClick={() => photoFiles.forEach(downloadResponseFile)}
                          >
                            Download Photos
                          </Button>
                        </Box>
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                            gap: 1
                          }}
                        >
                          {photoFiles.slice(0, 8).map((file, index) => {
                            const fileUrl = getResponseFileUrl(file);
                            const hiddenCount = photoFiles.length - 8;
                            return (
                              <Box
                                key={file.id || fileUrl || getResponseFileName(file)}
                                onClick={() => setSelectedResponseMedia(toMediaViewerItem(file))}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    setSelectedResponseMedia(toMediaViewerItem(file));
                                  }
                                }}
                                sx={{
                                  position: 'relative',
                                  aspectRatio: '4 / 3',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  bgcolor: 'grey.100',
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '&:hover img': { transform: 'scale(1.04)' }
                                }}
                              >
                                <Box
                                  component="img"
                                  src={fileUrl}
                                  alt={getResponseFileName(file)}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    display: 'block',
                                    transition: 'transform 0.2s ease'
                                  }}
                                />
                                {index === 7 && hiddenCount > 0 && (
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      inset: 0,
                                      bgcolor: 'rgba(0, 0, 0, 0.58)',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontWeight: 700
                                    }}
                                  >
                                    +{hiddenCount} more
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                          Click a photo to view it here with previous and next arrows.
                        </Typography>
                      </Box>
                    )}

                    {documentFiles.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {documentFiles.length} document{documentFiles.length === 1 ? '' : 's'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {documentFiles.map((file) => (
                            <Box
                              key={file.id || getResponseFileUrl(file) || getResponseFileName(file)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1.5,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 1.25
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                                <InsertDriveFile color="action" />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap title={getResponseFileName(file)}>
                                    {getResponseFileName(file)}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {file.category || 'document'}{file.uploadedAt ? ` • ${formatDate(file.uploadedAt)}` : ''}
                                  </Typography>
                                </Box>
                              </Box>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<Download />}
                                onClick={() => downloadResponseFile(file)}
                              >
                                Download
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                );
              })()
            )}
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Your Original Request
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {responseDialogRequest?.request_message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {safeArray(responseDialogRequest?.assessment_items).map((item) => (
              <Chip
                key={item}
                size="small"
                label={ASSESSMENT_OPTIONS.find((option) => option.value === item)?.label || item}
                variant="outlined"
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDialogRequest(null)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<AutoAwesome />}
            onClick={() => {
              const request = responseDialogRequest;
              setResponseDialogRequest(null);
              if (request) handleBuildBidFromAssessment(request);
            }}
          >
            Build Quotation Now
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={shareToastOpen}
        autoHideDuration={2500}
        onClose={() => setShareToastOpen(false)}
        message="Project link copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

    </Container>

    <MediaViewer
      open={Boolean(selectedResponseMedia)}
      onClose={() => setSelectedResponseMedia(null)}
      media={selectedResponseMedia}
      allMedia={responseFiles.filter(isResponseImage).map(toMediaViewerItem)}
    />

    {/* Reference-photo gallery: click a thumbnail to open in-app and page
        through all the owner's photos with next/prev — no new browser tabs. */}
    <MediaViewer
      open={Boolean(selectedBriefPhoto)}
      onClose={() => setSelectedBriefPhoto(null)}
      media={selectedBriefPhoto}
      allMedia={briefPhotos}
      allowDownload={false}
    />

    {/* AI Bid Generator */}
    {selectedProject && (
      <AIBidGenerator
        open={aiBidDialog}
        onClose={() => setAiBidDialog(false)}
        projectId={getProjectId(selectedProject)}
        clientId={selectedProject.client_id || selectedProject.owner_id}
        projectContext={{
          name: selectedProject.title || selectedProject.name,
          type: selectedProject.category || selectedProject.project_type || selectedProject.projectType,
          location: selectedProject.city || selectedProject.location,
          description: selectedProject.description || selectedProject.project_description,
          budget: selectedProject.total_budget || selectedProject.budget || selectedProject.estimated_budget,
          currency: selectedProject.currency || 'USD',
          proposalDeadline: selectedProject.proposal_deadline,
          assessmentRequest: selectedProject.assessmentRequest || null,
          fundingPreference: parseStructuredBrief(selectedProject.structured_brief)?.fundingPreference || null
        }}
        onBidCreated={() => {
          setSuccess('Draft saved to your Quotations page. Review and submit it when you are ready.');
          setAiBidDialog(false);
          localStorage.setItem('eqaya_first_proposal_submitted', 'true');
          loadMarketplace();
          // Success message stays visible until user dismisses it via the X button
        }}
        onRequestAssessment={() => {
          setAiBidDialog(false);
          if (selectedProject) {
            handleOpenAssessmentRequest(selectedProject);
          }
        }}
      />
    )}

    {/* Reschedule Site Visit — one step, records what was re-agreed in chat */}
    <Dialog
      open={Boolean(rescheduleRequest)}
      onClose={() => !rescheduling && setRescheduleRequest(null)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        Reschedule Site Visit
        <Typography variant="body2" color="text.secondary">
          {rescheduleRequest?.project_title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {rescheduleError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRescheduleError('')}>{rescheduleError}</Alert>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Agreed a new time with the owner? Set it here — they'll be notified immediately.
        </Typography>
        <TextField
          fullWidth
          type="datetime-local"
          label="New visit date & time"
          value={rescheduleTime}
          onChange={(e) => setRescheduleTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="Zimbabwe (Harare) time"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setRescheduleRequest(null)} disabled={rescheduling}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmitReschedule}
          disabled={rescheduling || !rescheduleTime}
        >
          {rescheduling ? 'Saving…' : 'Reschedule Visit'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Complete Site Visit — proof photos + bid-by commitment */}
    <Dialog
      open={Boolean(completeVisitRequest)}
      onClose={() => !completingVisit && setCompleteVisitRequest(null)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Complete {visitLabelFor(completeVisitRequest)}
        <Typography variant="body2" color="text.secondary">
          {completeVisitRequest?.project_title}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {isCallVisit(completeVisitRequest)
            ? 'Confirm the call happened and tell the owner when to expect your quotation.'
            : 'The owner may be far from the property. Photos from the site show them you were really there, and your quote-by date tells them when to expect your price.'}
        </Alert>
        {completeVisitError && (
          <Alert severity="error" sx={{ mb: 2 }}>{completeVisitError}</Alert>
        )}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {isCallVisit(completeVisitRequest) ? 'Photos (optional)' : 'Photos from the site (required, up to 5)'}
        </Typography>
        <Button variant="outlined" component="label" startIcon={<Image />} disabled={completeVisitPhotos.length >= 5}>
          Add Photos
          <input type="file" hidden accept="image/*" multiple onChange={handleCompleteVisitPhotoChange} />
        </Button>
        {completeVisitPhotos.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
            {completeVisitPhotos.map((file, index) => (
              <Box key={`${file.name}-${index}`} sx={{ position: 'relative' }}>
                <Box
                  component="img"
                  src={URL.createObjectURL(file)}
                  alt={file.name}
                  sx={{ width: 84, height: 84, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                />
                <IconButton
                  size="small"
                  onClick={() => setCompleteVisitPhotos((prev) => prev.filter((item, itemIndex) => itemIndex !== index))}
                  sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
        <TextField
          label="I will send my quotation by"
          type="date"
          value={completeVisitBidBy}
          onChange={(event) => setCompleteVisitBidBy(event.target.value)}
          fullWidth
          sx={{ mt: 3 }}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 10),
            max: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
          }}
          helperText="The owner is told this date. If it passes without a quotation, you both get a reminder."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setCompleteVisitRequest(null)} disabled={completingVisit}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<CheckCircle />}
          onClick={handleSubmitCompleteVisit}
          disabled={completingVisit || (!isCallVisit(completeVisitRequest) && completeVisitPhotos.length === 0) || !completeVisitBidBy}
        >
          {completingVisit ? 'Submitting…' : `Confirm ${isCallVisit(completeVisitRequest) ? 'Call' : 'Visit'} Complete`}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Profile Gate Dialog */}
    <ProfileGateDialog
      open={showGateDialog}
      onClose={closeGateDialog}
      action={gateAction}
      profileData={profileData}
    />
    </>
  );
};

export default ContractorMarketplacePage;
